import { RepositoryFactory } from "@/repositories/RepositoryFactory";
import Utils from "@/utils/Utils";
import router from "@/router";

const HomeworksRepository = RepositoryFactory.get("homeworks");

/**
 * Check if question is complete.
 * If question has no answer, consider complete.
 * If no answer found, consider incomplete.
 * If answer found for this question, consider complete.
 *
 * @param {object} question
 * @param {object} studentAnswers
 * @returns Boolean
 */
function is_complete_question(question, studentAnswers) {
  // skip if the question doesn't have answers (consider complete)
  if (question.answers.length === 0) {
    return true;
  }

  // get answer for associated question
  let qId = question.question.id;
  if (studentAnswers !== undefined) {
    let sAnswer = studentAnswers.find((studentAnswer) => {
      return studentAnswer.question_id === qId;
    });

    return sAnswer !== undefined;
  } else {
    return false;
  }
}

const state = {
  teacherHomeworks: [],
  homeworkIsForAllClass: false,
  is_mock_exam: false,
  homework: {
    exercises: [],
  },
  homework_exercises_incomplete: [],
  homeworks: [],
  currentExerciseKey: 0,
  studentData: {
    answers: {},
    mark: null,
  },
  /**
   * TODO: for better exercise change detection in training and homeworks.
   * loadingExercise is set to true at "train again" click in training or an exercise change in homeworks.
   */
  // loadingExercise: false
};

const getters = {
  getHomework: (state) => {
    return state.homework;
  },
  currentExerciseStudentAnswers: (state, getters, rootState) => {
    const currentExerciseId = rootState.exercises.exercise.exercise_id;
    const studentAnswers = state.studentData.answers;

    if (
      currentExerciseId !== undefined &&
      studentAnswers[currentExerciseId] !== undefined
    ) {
      return studentAnswers[currentExerciseId];
    } else {
      return [];
    }
  },
};

const actions = {
  createHomework({ dispatch, commit }, homework) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    const startDate = homework.homework.startDate;
    const startTime = homework.homework.startTime;
    const startDatetime = new Date(`${startDate}T${startTime}`).toISOString();
    homework.homework["start_datetime"] = startDatetime;

    const endDate = homework.homework.endDate;
    const endTime = homework.homework.endTime;
    const endDatetime = new Date(`${endDate}T${endTime}`).toISOString();
    homework.homework["end_datetime"] = endDatetime;

    delete homework.homework["startDate"];
    delete homework.homework["startTime"];
    delete homework.homework["endDate"];
    delete homework.homework["endTime"];

    homework.exercises = homework.exercises.map((exercise, index) => {
      return {
        id: exercise.exercise_id,
        order: index,
        help_enabled: exercise.help_enabled,
      };
    });

    return HomeworksRepository.createHomework(homework)
      .then(() => {
        commit("CLEAR_HOMEWORK_IS_FOR_ALL_CLASS");
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.homework.creationSuccess`),
          },
          { root: true },
        );
      })
      .catch((error) => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(`message.notifications.homework.creationFailure`),
          },
          { root: true },
        );

        throw error;
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  /**
   * Method to load student homework
   * @param {*} param0
   * @param {*} payload
   * @returns Promise
   */
  loadHomework({ commit, dispatch, state }, payload) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    return HomeworksRepository.getHomework(payload.homeworkId)
      .then((response) => {
        let homework = response.data;

        if (homework.exercises.length == 0) {
          throw new Error("Must contain exercises");
        }

        commit("SAVE_HOMEWORK", homework);
        commit("RESET_INCOMPLETE_HOMEWORK", homework);
        commit("SET_INCOMPLETE_HOMEWORK", homework);

        if ("student_answers" in homework) {
          for (const student_answer of homework["student_answers"]) {
            commit("SAVE_USER_INPUT", student_answer);
          }
        }

        dispatch("check_incomplete_homework");

        if (
          "ongoing_exercise_order" in homework &&
          homework.ongoing_exercise_order != null
        ) {
          commit(
            "SAVE_CURRENT_EXERCISE_KEY",
            homework.ongoing_exercise_order - 1,
          );
        }

        dispatch(
          "exercises/setExercise",
          homework.exercises[state.currentExerciseKey].exercise,
          {
            root: true,
          },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  /**
   * Method to load student test of mock exam for teacher
   * @param {*} param0
   * @param {*} payload
   * @returns Promise
   */
  loadHomeworkStudentTestForTeacher({ commit, dispatch, state }, payload) {
    const dispatchPayload = {
      studentId: payload.studentId,
    };
    return HomeworksRepository.getHomeworkStudentTestForTeacher(
      payload.homeworkId,
      dispatchPayload,
    ).then((response) => {
      let homework = response.data;
      commit("SAVE_HOMEWORK", homework);

      commit("CLEAR_STUDENT_ANSWERS");
      if ("student_answers" in homework) {
        for (const student_answer of homework["student_answers"]) {
          commit("SAVE_USER_INPUT", student_answer);
        }
      }

      dispatch(
        "exercises/setExercise",
        homework.exercises[state.currentExerciseKey].exercise,
        {
          root: true,
        },
      );
    });
  },
  loadTeacherHomeworks(context, payload) {
    if ("loading" in payload && payload.loading) {
      context.dispatch("loadings/enableLoadings", {}, { root: true });
    }
    delete payload.loading;

    HomeworksRepository.getTeacherHomeworks(payload)
      .then((response) => {
        const homeworks = response.data.results;
        const totalPages = response.data.total_pages;

        context.commit("SAVE_TEACHER_HOMEWORKS", homeworks);
        context.commit("pagination/SET_TOTAL_PAGES", totalPages, {
          root: true,
        });
      })
      .catch(() => {
        context.dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.homework.retrieveHomeworksError`,
            ),
          },
          { root: true },
        );
      })
      .finally(() => {
        context.dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  deleteHomework(context, homeworkId) {
    context.dispatch("loadings/enableLoadings", {}, { root: true });

    return HomeworksRepository.deleteHomework(homeworkId)
      .then(() => {
        context.commit("SAVE_HOMEWORK", {});
        context.commit("REMOVE_TEACHER_HOMEWORK", homeworkId);

        context.dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(
              `message.notifications.homework.deleteHomeworkSuccess`,
            ),
          },
          { root: true },
        );
      })
      .catch(() => {
        context.dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.homework.deleteHomeworkError`,
            ),
          },
          { root: true },
        );
      })
      .finally(() => {
        context.dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  check_incomplete_homework({ commit, state }) {
    state.homework.exercises.forEach((e) => {
      var exercise = e.exercise;
      var exerciseId = exercise.exercise_id;
      var studentAnswers = state.studentData.answers[exerciseId];
      var isComplete = true;

      // check exercise only if is in incomplete state
      if (state.homework_exercises_incomplete.indexOf(exerciseId) > -1) {
        exercise.exercise_parts.every((part) => {
          // check each subparts, stops at first falsy value
          part.subparts.every((subpart) => {
            subpart.questions.every((question) => {
              isComplete = is_complete_question(question, studentAnswers);
              return isComplete;
            });
            return isComplete;
          });

          // check each question in part, stops at first falsy value
          part.questions.every((question) => {
            isComplete = is_complete_question(question, studentAnswers);
            return isComplete;
          });

          // If exercise complete, remove from incomplete list of exercises
          if (isComplete) {
            commit("SET_COMPLETE_EXERCISE", exerciseId);
          }
          return isComplete;
        });
      }
    });
  },
  saveUserInput({ commit, dispatch }, payload) {
    // Only save user input if homework is defined
    if ("id" in state.homework) {
      commit("SAVE_USER_INPUT", payload);

      // Only check if homework is incomplete for students
      if (this.getters["users/isStudent"]) {
        dispatch("check_incomplete_homework");
      }
    }
  },
  saveStudentMark({ commit }, mark) {
    if ("id" in state.homework) {
      commit("SAVE_STUDENT_MARK", mark);
    }
  },
  /**
   * Send request to save user inputs in a homework.
   * The data sent is the data contained in state.studentData.answers for the current exercise.
   *
   * If questionId defined, only send data for this question.
   *
   * @param {*} param0
   * @param {String} questionId (UUID)
   */
  saveHomeworkState({ getters, dispatch, state }, questionId = null) {
    // Only save homework state for students
    if (!this.getters["users/isStudent"]) {
      return;
    }

    if ("id" in state.homework) {
      let student_answers = getters.currentExerciseStudentAnswers;
      if (questionId) {
        student_answers = student_answers.filter(
          (sa) => sa.question_id === questionId,
        );
      }

      const payload = {
        student_answers: student_answers,
      };

      HomeworksRepository.saveHomeworkState(state.homework.id, payload)
        .then(() => {})
        .catch((error) => {
          // In case of data error, redirect to homeworks list
          // and show error message
          if (error.response.status == 422) {
            // mismatched data error (HTTP 422)
            // means that the homework data in the frontend
            // is not the same as the one in the backend
            dispatch(
              "notifications/addError",
              {
                message: state.$t(
                  `message.notifications.homework.mismatchedHomeworkData`,
                ),
              },
              { root: true },
            );
            router.push({ name: "StudentHomeworks" });
          } else {
            dispatch(
              "notifications/addError",
              {
                message: state.$t(
                  `message.notifications.homework.saveHomeworkError`,
                ),
              },
              { root: true },
            );
          }
        })
        .finally(() => {
          dispatch("loadings/disableLoadings", {}, { root: true });
        });
    }
  },
  updateStatus({ commit, dispatch }, payload) {
    HomeworksRepository.updateStatus(payload.homeworkId, payload.data)
      .then((response) => {
        commit("SAVE_HOMEWORK_PUBLISHED", response.data);
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.homework.creationSuccess`),
          },
          { root: true },
        );
      })
      .catch((error) => {
        let message = "";
        commit("REVERT_HOMEWORK_PUBLISHED", payload.homeworkId);

        if (error.response.status == 403) {
          message = state.$t(`message.notifications.homework.alreadyStarted`);
          if (error.response.data.ciphertext) {
            commit(
              "SAVE_HOMEWORK_PUBLISHED",
              Utils.aesDecrypt(error.response.data.ciphertext).data,
            );
          } else {
            commit("SAVE_HOMEWORK_PUBLISHED", error.response.data.data);
          }
        } else {
          message = state.$t(
            `message.notifications.homework.saveHomeworkError`,
          );
        }
        dispatch(
          "notifications/addError",
          {
            message: message,
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  /**
   * Update the current exercise key in the homework exercises list,
   * and send request to server to save the current exercise server-side
   * (for the current homework and the current student)
   * @param {*} param0
   * @param {Int} nextExerciseKey
   */
  updateExerciseKey({ commit, dispatch }, nextExerciseKey) {
    dispatch("loadings/enableCurrentExerciseLoading", {}, { root: true });
    commit("SAVE_CURRENT_EXERCISE_KEY", nextExerciseKey);

    const payload = {
      target_exercise_id:
        state.homework.exercises[nextExerciseKey].exercise.exercise_id,
    };

    HomeworksRepository.saveCurrentExercise(state.homework.id, payload)
      .then(() => {})
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.homework.saveHomeworkError`,
            ),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
        dispatch("loadings/disableCurrentExerciseLoading", {}, { root: true });
      });
  },
  completeHomework({ dispatch, state }, payload) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    return HomeworksRepository.completeHomework(
      payload.homeworkId,
      payload.data,
    ).catch((error) => {
      let message = "";

      if (error.response.status == 403) {
        message = state.$t(`message.notifications.homework.expired`);
      } else {
        message = state.$t(`message.notifications.homework.saveHomeworkError`);
      }

      dispatch(
        "notifications/addError",
        {
          message: message,
        },
        { root: true },
      );

      throw error;
    });
  },
  listStudentHomeworks({ commit, dispatch, state }, payload) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    return HomeworksRepository.listStudentHomeworks(payload)
      .then((response) => {
        const homeworks = response.data.results;
        const totalPages = response.data.total_pages;

        commit("ADD_HOMEWORKS_TO_LIST", homeworks);
        commit("pagination/SET_TOTAL_PAGES", totalPages, {
          root: true,
        });
      })
      .catch(() => {
        dispatch(
          "notifications/addError",
          {
            message: state.$t(
              `message.notifications.homework.retrieveHomeworksError`,
            ),
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  updateHomework({ dispatch, commit }, homework) {
    dispatch("loadings/enableLoadings", {}, { root: true });
    const startDate = homework.homework.startDate;
    const startTime = homework.homework.startTime;
    const startDatetime = new Date(`${startDate}T${startTime}`).toISOString();
    homework.homework["start_datetime"] = startDatetime;

    const endDate = homework.homework.endDate;
    const endTime = homework.homework.endTime;
    const endDatetime = new Date(`${endDate}T${endTime}`).toISOString();
    homework.homework["end_datetime"] = endDatetime;

    delete homework.homework["startDate"];
    delete homework.homework["startTime"];
    delete homework.homework["endDate"];
    delete homework.homework["endTime"];

    homework.exercises = homework.exercises.map((exercise, index) => {
      return {
        id: exercise.exercise_id,
        order: index,
        help_enabled: exercise.help_enabled,
      };
    });

    return HomeworksRepository.updateHomework(homework.id, homework)
      .then(() => {
        commit("CLEAR_HOMEWORK_IS_FOR_ALL_CLASS");
        dispatch(
          "notifications/addSuccess",
          {
            message: state.$t(`message.notifications.homework.updateSuccess`),
          },
          { root: true },
        );
      })
      .catch((error) => {
        let message = "";

        if (error.response.status == 403) {
          message = state.$t(`message.notifications.homework.alreadyStarted`);
        } else {
          message = state.$t(`message.notifications.homework.updateFailure`);
        }

        dispatch(
          "notifications/addError",
          {
            message: message,
          },
          { root: true },
        );
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  getHomeworkForTeacher({ commit, dispatch }, homeworkId) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    return HomeworksRepository.getHomeworkForTeacher(homeworkId)
      .then((response) => {
        let homework = response.data;
        const startDateSeparatorIndex = homework["start_date"].indexOf("T");
        const endDateSeparatorIndex = homework["end_date"].indexOf("T");
        homework["startDate"] = homework["start_date"].substr(
          0,
          startDateSeparatorIndex,
        );
        homework["startTime"] = homework["start_date"].substr(
          startDateSeparatorIndex + 1,
          5,
        );
        homework["endDate"] = homework["end_date"].substr(
          0,
          endDateSeparatorIndex,
        );
        homework["endTime"] = homework["end_date"].substr(
          endDateSeparatorIndex + 1,
          5,
        );
        delete homework.start_date;
        delete homework.end_date;

        commit("SAVE_HOMEWORK", homework);
      })
      .finally(() => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      });
  },
  completeUnfinishedClassHomeworks({ dispatch }, params) {
    dispatch("loadings/enableLoadings", {}, { root: true });

    return HomeworksRepository.completeUnfinishedClassHomeworks(params).finally(
      () => {
        dispatch("loadings/disableLoadings", {}, { root: true });
      },
    );
  },
  clearHomeworks({ commit }) {
    commit("CLEAR_HOMEWORKS");
  },
  clearHomework({ commit }) {
    commit("CLEAR_HOMEWORK");
  },
  clearStudentAnswers({ commit }) {
    commit("CLEAR_STUDENT_ANSWERS");
  },
  clearStudentData({ commit }) {
    commit("CLEAR_STUDENT_DATA");
  },
  setHomeworkIsForAllClass({ commit }, value) {
    commit("SAVE_HOMEWORK_IS_FOR_ALL_CLASS", value);
  },
  clearHomeworkIsForAllClass({ commit }) {
    commit("CLEAR_HOMEWORK_IS_FOR_ALL_CLASS");
  },
  setIsMockTest({ commit }, value) {
    commit("SET_IS_MOCK_TEST", value);
  },
};

const mutations = {
  SAVE_HOMEWORK(state, homework) {
    state.homework = homework;
  },
  SET_INCOMPLETE_HOMEWORK(state) {
    // set all exercises as incomplete
    state.homework.exercises.forEach((e) => {
      state.homework_exercises_incomplete.push(e.exercise.exercise_id);
    });
  },
  SAVE_TEACHER_HOMEWORKS(state, homeworks) {
    state.teacherHomeworks = homeworks;
  },
  SAVE_HOMEWORKS(state, homeworks) {
    state.homeworks = homeworks;
  },
  ADD_HOMEWORKS_TO_LIST(state, homeworks) {
    // security check: avoid duplicates
    for (let h of homeworks) {
      if (state.homeworks.findIndex((item) => item.id === h.id) === -1) {
        state.homeworks.push(h);
      }
    }
  },
  SAVE_USER_INPUT(state, payload) {
    const exerciseId = payload.exercise_id;

    if (state.studentData.answers[exerciseId] === undefined) {
      state.studentData.answers[exerciseId] = [];
    }

    delete payload.exercise_id;
    state.studentData.answers[exerciseId].push(payload);
  },
  SAVE_STUDENT_MARK(state, mark) {
    state.studentData.mark = mark;
  },
  SAVE_CURRENT_EXERCISE_KEY(state, key) {
    let targetKey =
      key >= state.homework.exercises.length
        ? state.homework.exercises.length - 1
        : key;
    targetKey = targetKey === -1 ? 0 : targetKey;

    state.currentExerciseKey = targetKey;
  },
  SAVE_HOMEWORK_PUBLISHED(state, homework) {
    var teacherHomeworksArray = state.teacherHomeworks;
    if (state.teacherHomeworks.includes("items")) {
      teacherHomeworksArray = state.teacherHomeworks.items;
    }
    for (const h of teacherHomeworksArray) {
      if (h.id == homework.id) h.is_published = homework.is_published;
    }
  },
  /**
   * Reverts the displayed published status of a homework to its actual store value.
   * Uses object spread to create a new reference, forcing Vue's reactivity to re-render
   * the component even though the underlying value hasn't changed.
   * Called when an updateStatus request fails, to ensure the UI stays in sync with the store.
   */
  REVERT_HOMEWORK_PUBLISHED(state, homeworkId) {
    var teacherHomeworksArray = state.teacherHomeworks;
    if (state.teacherHomeworks.includes("items")) {
      teacherHomeworksArray = state.teacherHomeworks.items;
    }
    const hIndex = teacherHomeworksArray.findIndex((h) => h.id == homeworkId);
    if (hIndex !== -1) {
      teacherHomeworksArray[hIndex] = { ...teacherHomeworksArray[hIndex] };
    }
  },
  REMOVE_TEACHER_HOMEWORK(state, homeworkId) {
    let i = state.teacherHomeworks.findIndex((h) => h.id === homeworkId);
    if (i !== -1) {
      state.teacherHomeworks.splice(i, 1);
    }
  },
  SET_COMPLETE_EXERCISE(state, exerciseId) {
    var index = state.homework_exercises_incomplete.indexOf(exerciseId);
    if (index > -1) {
      state.homework_exercises_incomplete.splice(index, 1);
    }
  },
  RESET_INCOMPLETE_HOMEWORK(state) {
    state.homework_exercises_incomplete = [];
  },
  CLEAR_HOMEWORKS(state) {
    state.homeworks = [];
  },
  CLEAR_HOMEWORK(state) {
    state.homework = {
      exercises: [],
    };
    state.currentExerciseKey = 0;
  },
  CLEAR_TEACHER_HOMEWORKS(state) {
    state.teacherHomeworks = [];
  },
  CLEAR_STUDENT_ANSWERS(state) {
    state.studentData.answers = {};
  },
  CLEAR_STUDENT_DATA(state) {
    state.studentData.answers = {};
    state.studentData.mark = null;
  },
  SAVE_HOMEWORK_IS_FOR_ALL_CLASS(state, value) {
    state.homeworkIsForAllClass = value;
  },
  SET_IS_MOCK_TEST(state, value) {
    state.is_mock_exam = value;
  },
  CLEAR_HOMEWORK_IS_FOR_ALL_CLASS(state) {
    state.homeworkIsForAllClass = false;
  },
};

export default {
  namespaced: true,
  getters,
  state,
  actions,
  mutations,
};
